var _ = require('underscore');
var TorsoView = require('backbone-torso/modules/View');
var SecureMessageDataBehavior = require('./../secure-message/SecureMessageDataBehavior');
var billDetailTemplate = require('./billDetailTemplate.hbs');
var PaymentDetailsFormModel = require('./../payment/PaymentDetailsFormModel');
var applicationUtils = require('./../applicationUtils');
var dateUtils = require('./../utils/dateUtils');
var demographicsFeatureConfigModel = require('./../demographics/demographicsFeatureConfigModel');
var DemographicsDataBehavior = require('./../demographics/DemographicsDataBehavior');
var launcherAppInterface = require('./../launcherApplicationInterface');
var financialFeatureService = require('./../financial/financialFeatureService');
var screenSizeView = require('./../widget/screen-size/screenSizeView');
var userService = require('./../user/userService');

var withSidebarClassNameModifier = '';
var PAYMENT_METHODS = PaymentDetailsFormModel.PAYMENT_METHODS;
var CARDS = PaymentDetailsFormModel.CARDS;
var PAYMENTS_DISABLED_REASON = financialFeatureService.PAYMENTS_DISABLED_REASON;
PAYMENTS_DISABLED_REASON.MISSING_ACCOUNT_NUMBER = 'MISSING_ACCOUNT_NUMBER';
PAYMENTS_DISABLED_REASON.MISSING_PATIENT_IDENTIFIER_AND_ACCOUNT_NUMBER = 'MISSING_PATIENT_IDENTIFIER_AND_ACCOUNT_NUMBER';

var IS_ON_ADMIN_TOOL = applicationUtils.currentApplication === applicationUtils.APPLICATIONS.ADMIN_TOOL;

if (applicationUtils.currentApplication === applicationUtils.APPLICATIONS.ADMIN_TOOL) {
  withSidebarClassNameModifier = ' brec-tab__content--side';
}

/**
 * View to display the details of a patients bills.
 *
 * @class BillDetailsView
 * @constructor
 */
module.exports = TorsoView.extend({
  template: billDetailTemplate,
  className: 'brec-tab__content' + withSidebarClassNameModifier,
  id: 'tab-content',
  moduleNamespace: 'bill-details',

  behaviors: {
    demographics: {
      behavior: DemographicsDataBehavior,
      returnSingleResult: true,
      renderOnFetch: true,
      id: { property: '_patientVecnaId' }
    },
    bill: {
      behavior: SecureMessageDataBehavior,
      returnSingleResult: true,
      renderOnFetch: true,
      alwaysFetch: true,
      id: { property: '_billId' }
    },
    receipts: {
      behavior: SecureMessageDataBehavior,
      renderOnFetch: true,
      alwaysFetch: true,
      // Change to:
      //   ids: { property: 'behaviors.bill.data:receiptMessageRetrievers[].id' }
      // After https://github.com/vecnatechnologies/backbone-torso/issues/305 is fixed.
      updateEvents: ['behaviors.bill.data:change:receiptMessageRetrievers', 'behaviors.bill:fetched:ids'],
      ids: function(cache) {
        var billDataBehavior = this.view.getBehavior('bill');
        var billData = billDataBehavior.data;

        var receiptIds;
        var accountNumber = billData.get('billDTO.accountNumber');
        if (accountNumber) {
          var criteria = {
            receiptCriteria: {
              accountNumbers: [accountNumber],
              transactionTypes: [ 'PAYMENT' ]
            },
            isPatient: true,
            vecnaIds: [this.view._patientVecnaId]
          };
          receiptIds = cache.fetchIdsByCriteria(criteria);
        }
        return receiptIds;
      }
    }
  },

  events: {
    'click [data-payment-button]': '_goToMakePaymentPage',
    'click [data-detail-pay-bill-button]': '_goToMakePaymentPage'
  },

  /**
   * Re-fetches the data used by this view.
   */
  fetchContent: function() {
    this.getBehavior('bill').retrieve();
  },

  /**
   * @method initialize
   * @override
   * @param options {Object} the options of this perspective.
   *   @param options.patientVecnaId {String} the patient id of the patient that this perspective is displaying.
   *   @param options.billId {String} the page of the list view to display.
   */
  initialize: function(options) {
    _.bindAll(this, 'render');
    this._patientVecnaId = options.patientVecnaId;
    this._billId = parseInt(options.billId);

    this.listenTo(this.viewState, 'change:isLargeScreen', this.render);
    this.listenTo(demographicsFeatureConfigModel, 'load-complete', this.render);
    this.listenTo(screenSizeView, 'change:screenSize', this._updateScreenSize);
    this.listenTo(this.getBehavior('bill'), 'fetched', this._markBillAsRead);
    this._updateScreenSize();

    demographicsFeatureConfigModel.loadOnce();
    financialFeatureService.fetchPayAbility(options.patientVecnaId).then(this.render);
  },

  /**
   * @method prepare
   * @override
   */
  prepare: function() {
    var context = TorsoView.prototype.prepare.apply(this);

    context.isModal = false;
    context.servicesId = this._billId;

    var hasAccountNumber = false;

    var billDataBehavior = this.getBehavior('bill');
    var bill = billDataBehavior.data.getModel();
    if (bill) {
      context.model = bill.toJSON();
      context.billVecnaId = bill.get('vecnaId');
      context.serviceDates = bill.getServiceDates();
      context.isPastDue = bill.isPastDue();
      context.isPaid = (bill.get('billDTO.externalBill.status') === bill.BILL_STATUSES.PAID);
      context.showPaymentProcessing = (bill.get('billDTO.externalBill.status') === bill.BILL_STATUSES.PAYMENT_PENDING);

      var isBillUnpaid = bill.get('billDTO.externalBill.status') === bill.BILL_STATUSES.UNPAID;
      var canAdminPay = isBillUnpaid && financialFeatureService.adminPaymentAllowed();
      var canPatientPay = isBillUnpaid && financialFeatureService.patientPaymentAllowed(this.patientVecnaId);
      context.showPaymentButton = canAdminPay || canPatientPay;

      _.each(bill.get('billDTO.patient.identifiers'), function(identifier) {
        if (identifier.sourceName === demographicsFeatureConfigModel.get('patientIdentifierDisplaySource')) {
          context.patientMrn = identifier.identifier;
        }
      });
      hasAccountNumber = !!bill.get('billDTO.accountNumber');
    }
    var receiptDataBehavior = this.getBehavior('receipts');
    var receiptModelsJSON = receiptDataBehavior.data.toJSON();
    if (receiptModelsJSON) {
      context.displayReceipts = _.map(receiptModelsJSON, function (receiptModelJSON) {

        if (receiptModelJSON && receiptModelJSON.payment) {
          _.extend(receiptModelJSON.payment, {
            CARDS: CARDS,
            PAYMENT_METHODS: PAYMENT_METHODS,
            currentApplication: applicationUtils.currentApplication,
            expirationYear: dateUtils.getExpirationYear(receiptModelJSON.payment.expirationDate),
            expirationMonth: dateUtils.getExpirationMonth(receiptModelJSON.payment.expirationDate),
            cardNumberLast4: receiptModelJSON.payment.last4
          });

          _.each(receiptModelJSON.payment.paymentDetails, function (paymentDetails) {
            if (paymentDetails) {
              paymentDetails.showDetails = !paymentDetails.billMessageVecnaId || paymentDetails.billMessageVecnaId === context.billVecnaId;
            }
          });
        }
        return receiptModelJSON;
      });
    }
    context.patientVecnaId = this._patientVecnaId;
    context.currentApplication = applicationUtils.currentApplication;
    context.isOnAdminTool = IS_ON_ADMIN_TOOL;
    context.isAtHospital = launcherAppInterface.isAtHospital();
    context.abilityToPayInfo = financialFeatureService.getAbleToPayState(this._patientVecnaId);

    var billPayDisabled = context.abilityToPayInfo.unableToPayReason === PAYMENTS_DISABLED_REASON.BILL_PAY_DISABLED;
    var missingPatientIdentifier = context.abilityToPayInfo.unableToPayReason === PAYMENTS_DISABLED_REASON.MISSING_PATIENT_IDENTIFIER;
    var missingAccountNumber = !hasAccountNumber;

    if (!billPayDisabled) {
      if (missingPatientIdentifier && missingAccountNumber) {
        context.abilityToPayInfo.unableToPayReason = PAYMENTS_DISABLED_REASON.MISSING_PATIENT_IDENTIFIER_AND_ACCOUNT_NUMBER;
      } else if (missingAccountNumber) {
        context.abilityToPayInfo.ableToPay = false;
        context.abilityToPayInfo.unableToPayReason = PAYMENTS_DISABLED_REASON.MISSING_ACCOUNT_NUMBER;
      }
    }
    return context;
  },

  /**
   * Go to make payment page
   * @param event {jQuery.Event} the click event that triggered this call.
   * @method _goToMakePaymentPage
   * @private
   */
  _goToMakePaymentPage: function(event) {
    if (event) {
      event.preventDefault();
      var $target = $(event.currentTarget);
      var targetUrl = $target.attr('data-url');
      window.location.href = targetUrl;
    }
  },

  /**
   * Detect whether it is in default (not large) screen view
   * @method _updateScreenSize
   * @private
   */
  _updateScreenSize: function() {
    var currentScreenIsLargerThanTablet = screenSizeView.currentSizeIsLarger(screenSizeView.SCREEN_SIZES.MEDIUM_LANDSCAPE);
    this.set('isLargeScreen', currentScreenIsLargerThanTablet);
  },

  /**
   * Mark this bill as read
   * @method _markBillAsRead
   */
  _markBillAsRead: function() {
    var bill = this.getBehavior('bill').data.getModel();
    bill.markAsRead();
    userService.messageCountsModel.fetch();
  }
});
